import { VirtuosoHandle } from 'react-virtuoso';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

export default function useVirtuosoScroll({
  virtuosoRef,
  containerRef
}: {
  virtuosoRef: RefObject<VirtuosoHandle>;
  containerRef: RefObject<HTMLDivElement>;
}) {
  const currentVirtuosoRangeRef = useRef<{ startIndex: number; endIndex: number }>({ startIndex: 0, endIndex: 0 });

  const scrollToVirtuosoIndex = useCallback(
    ({ paragraphIndex, segmentId, wordId }: { paragraphIndex: number; segmentId: string; wordId?: number }) => {
      if (paragraphIndex === -1) {
        return;
      }
      if (
        !currentVirtuosoRangeRef.current ||
        currentVirtuosoRangeRef.current.startIndex > paragraphIndex ||
        currentVirtuosoRangeRef.current.endIndex < paragraphIndex
      ) {
        virtuosoRef.current?.scrollToIndex({
          index: paragraphIndex,
          align: 'center',
          behavior: 'auto'
        });
      }
      if (wordId === undefined || wordId < 0) {
        return;
      }
      requestAnimationFrame(() => {
        const firstSearchedWordElement = containerRef.current?.querySelector(
          `[data-word-id="${wordId}"][data-segment-id="${segmentId}"]`
        );
        if (!firstSearchedWordElement) {
          return;
        }
        firstSearchedWordElement.scrollIntoView({
          behavior: 'auto',
          block: 'center'
        });
      });
    },
    [containerRef, virtuosoRef]
  );

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.FullTranscriptRangeChanged, ({ startIndex, endIndex }) => {
      currentVirtuosoRangeRef.current = { startIndex, endIndex };
    });

    return () => {
      EventBus.off(CustomEvents.FullTranscriptRangeChanged, eventListener);
    };
  }, []);

  return { scrollToVirtuosoIndex };
}
